/**
 * **The movement checkpoint-consumer registry** — the `REACTION_OFFERS` sibling for the OTHER
 * pre-commit interception window: a **declared move**, not a triggering attack. Where {@link
 * import("./reactionRegistry").REACTION_OFFERS} consults its fixed list at the "damage rolled, not
 * yet committed" window, this consults its own fixed list at the "move declared, not yet applied"
 * window (RAW: an Overwatch SKIRMISH resolves "before they move" — the whole reason this seam
 * exists).
 *
 * **Adding a consumer is a plain append to {@link MOVE_CHECKPOINT_CONSUMERS}** — never a rewire of
 * this module's seam: `gatherMoveCheckpoint`'s signature, the prompt-id convention, and the
 * sort/consult loop stay fixed.
 *
 * **Why a two-level shape (`MoveCheckpointConsumer.compile` → `MoveCheckpointOffer[]`), not a
 * flat offer list like `REACTION_OFFERS`:** Brace has exactly one decider per triggering attack
 * (the target). A movement checkpoint consumer (Overwatch) can have MANY simultaneous deciders —
 * every hostile watcher who threatens the mover's `from` cell — so each registered consumer
 * *compiles* zero or more offers (one per eligible watcher) against the live state, mirroring
 * `reactionRegistry.ts`'s `compileProtectorOffers` dynamic-compile shape rather than its static
 * `BRACE_OFFER` constant. The registry then flattens every consumer's offers into ONE
 * deterministically-ordered queue before consulting them one at a time.
 *
 * **Deterministic order: initiative (`state.order` position), then id** — the initiative tier of
 * the trigger bus's F2a order (`triggerBus.ts`, `decisions/0212` item 2), keyed on the deciding
 * watcher and without the priority and gather tiers this simpler window doesn't need. A watcher off
 * `state.order` (defensive) sorts last.
 *
 * **On the event loop** (`event-engine-lifecycle` 5.1): the referee raises `move_declared` into the
 * call's loop (`referee/moveDeclared.ts`), and the consumers' compiled offers are that event's
 * interrupts, consulted by {@link gatherMoveCheckpoint} as its trigger stage. The consumer list
 * stays the data the interrupts are compiled from (and the seam a test injects through
 * `RefereeCtx.checkpointConsumers`).
 */

import {
  DISENGAGED_STATUS,
  feintedBy,
  hasStatus,
  isActor,
  sideOf,
  worldFor,
  type Cell,
  type EncounterState,
  type RecordMoveReactionDeclineCommand,
} from "@lancer-vtt/domain";
import {
  NeedsDecisionSignal,
  SEED_OVERLAY,
  cannotMakeAttacks,
  evaluateMatch,
  evaluatePredicate,
  gatherStatusHandlers,
  type LoadedOverlay,
  type PredicateContext,
} from "./effects";
import { reactionAvailable } from "./reactionRegistry";
import { COVERING_FIRE_REACTION } from "./economy/coveringFire";
import { hasLiveMoveReactionDecline } from "./moveReactionDecline";
import { formatOverwatchReach, overwatchReachAt, type OverwatchReach } from "./overwatchReach";
import { INTERRUPT_CONSUMER, INTERRUPT_DECLINE_OPTION } from "./moveInterrupts";
import type { DiceSource } from "../dice";
import type { EncounterIntent } from "@lancer-vtt/shared";

/**
 * The facts a checkpoint consumer reads to decide whether (and for whom) to raise an offer
 * against a declared move. Pure — {@link gatherMoveCheckpoint} never mutates `state`, and the
 * same `ctx` re-consulted against the same `choices` always produces the same result (the
 * replay-determinism contract).
 */
export type MoveCheckpointContext = {
  state: EncounterState;
  /**
   * The declared move this checkpoint gathers against — the mover, its pre-move anchor, and its
   * declared destination. `path` is the route the move walks (the chosen one, or the engine's), and
   * `spaces` its step count (`landing-zone-full-automation` Q6); with no route (a move the engine
   * can't route, such as a GM repositioning a non-actor), a move's distance is the straight from-to
   * line. `voluntary: false` marks a walk someone else caused (Puppet System); absent, the move is the mover's own.
   */
  declaredMove: { moverId: string; from: Cell; to: Cell; path?: readonly Cell[]; spaces?: number; voluntary?: false };
  /** Recorded answers (`intent.choices`) — a watcher's decline/take answer records under its offer's prompt id. */
  choices: Record<string, string> | undefined;
  /** Interactive mode: an eligible, unanswered offer throws to round-trip a prompt (else defaults to decline). */
  interactive: boolean;
  /**
   * The authority's dice — threaded for parity with {@link import("./reactionRegistry").
   * ReactionContext} and for a future consumer whose eligibility or outcome needs a draw. **No
   * consumer draws through this today, and the checkpoint gather itself never rolls**: a
   * checkpoint offer's "take" is a SIBLING resolution (its own `request_attack`, its own dice)
   * run to completion by the watcher's client BEFORE it re-submits the recorded answer.
   */
  dice: DiceSource;
  /** The loaded overlay, for a future consumer that reads authored content off it. Omitted → the caller's own default (mirrors every other seed read-site's convention). */
  overlay?: LoadedOverlay;
};

/**
 * One decider's offer against the declared move — a single watcher's chance to halt the mover's
 * checkpoint gather for a decision. Built by a {@link MoveCheckpointConsumer}'s `compile`, one
 * per eligible watcher (or eligible whatever-the-consumer's-decider-unit-is; every consumer today
 * decides on a watcher, so the field is named for that case).
 */
export type MoveCheckpointOffer = {
  /**
   * Stable id for THIS (consumer, decider) pairing — used only for the deterministic-order
   * tiebreak (see this module's header doc). Not the prompt id (that's {@link promptId}, built
   * via {@link moveCheckpointPromptId} so cross-actor routing recognizes it).
   */
  readonly id: string;
  /** The combatant this offer is keyed to — drives the initiative-order sort AND (by construction) {@link who}. */
  readonly watcherId: string;
  /** Which actor must answer (the cross-actor `who` field). Always the watcher — the checkpoint's decider is never the mover itself. */
  who(ctx: MoveCheckpointContext): string;
  /** The stable prompt id the answer records under — MUST be built via {@link moveCheckpointPromptId} (the backend's cross-actor attribution seam recognizes prompts by this shape, mirroring `isReactionPromptId`'s). */
  promptId(ctx: MoveCheckpointContext): string;
  /** The human-readable prompt the decider sees. */
  prompt(ctx: MoveCheckpointContext): string;
  /** The reaction's short name (`Overwatch`) — see `reactionRegistry.ts`'s `ReactionOffer.title`. Optional so a test consumer needn't author one. */
  readonly title?: string;
  /** The selectable options (the recorded answer is one of these ids). */
  readonly options: { id: string; label: string }[];
  /** Overwatch offers only: how far the mover's `from` is, and which of the watcher's weapons reach it (never empty on an offer). */
  readonly overwatchReach?: OverwatchReach;
};

/**
 * One registered checkpoint-consumer type (Overwatch is the built-in one). A fixed, authored
 * member of {@link MOVE_CHECKPOINT_CONSUMERS} — never
 * registration order — so the gather is deterministic and replay-stable. `compile` reads the
 * live `ctx` and returns every offer this consumer type currently has live (zero when nothing's
 * eligible); {@link gatherMoveCheckpoint} flattens every registered consumer's offers together
 * before sorting and consulting them.
 */
export type MoveCheckpointConsumer = {
  /** Stable id — namespaces this consumer's offers' prompt ids (see {@link moveCheckpointPromptId}). */
  readonly id: string;
  /** Pure: compile every currently-eligible offer this consumer type raises against the declared move. */
  compile(ctx: MoveCheckpointContext): MoveCheckpointOffer[];
};

/**
 * The hypothetical footprint `size` would occupy anchored at `cell` — the mover's PRE-move
 * `from` anchor, mirrors {@link import("./mine").footprintAt} (`applyMove` never writes `from`
 * onto any combatant's live `pos`, so the eligibility test measures against {@link
 * MoveCheckpointContext.declaredMove}'s own `from` fact, never a live `pos` read — the pure-
 * over-`(state, declaredMove)` replay-determinism contract this module's header doc names).
 */
function moverFootprintAtFrom(state: EncounterState, from: Cell, size: number): Cell[] {
  return worldFor(state).footprint(from, size).cells();
}

/** The consumer id (also the named reaction-cap key the manual path already spends, `economy.ts`'s `reactionId: "overwatch"` — offer-taken and manually-fired Overwatch share one cap for free). */
export const OVERWATCH_CHECKPOINT_ID = "overwatch";
/** The recorded-answer option ids the Overwatch offer presents. "Take" does not itself resolve the shot — see this consumer's own doc. */
export const OVERWATCH_TAKE_OPTION = "take";
export const OVERWATCH_DECLINE_OPTION = "decline";

/**
 * **The built-in Overwatch move-checkpoint offer.** Compiles one {@link MoveCheckpointOffer} per
 * eligible watcher against a declared move — every combatant where:
 *   (a) **hostile** to the mover ({@link sideOf} differs);
 *   (b) **placed** on the map (a footprint to threaten from);
 *   (c) the mover's **`from`** cell (not its live `pos` — see {@link moverFootprintAtFrom}) is
 *       within the THREAT of at least one of the watcher's {@link
 *       import("./overwatchReach").overwatchWeaponThreats} (Ordnance- and Superheavy-excluded, per RAW p.105 and p.70). The
 *       offer carries that {@link OverwatchReach} (distance + the reaching weapons) and names it in
 *       its prompt;
 *   (d) {@link reactionAvailable} for the watcher's named `"overwatch"` reaction (folds in the
 *       global reaction slot, the 1/round named cap, `checkReactionStatusLegality`'s Jammed/
 *       Grappled/Stunned/Down-and-Out wall, AND the held-action lockout — the ONE eligibility
 *       choke point `reactionRegistry.ts` already centralizes for Brace, reused verbatim here);
 *   (e) the **mover** is not {@link DISENGAGED_STATUS} (RAW: Disengaged movement "does not
 *       provoke reactions" — mirrors the room's own manual-Overwatch-vs-Disengaged-target gate,
 *       `backend/src/encounterRoom.ts`'s `request_attack {reaction:true}` handling);
 *   (f) the **watcher** is an {@link isActor} — `kind` ∈ `mech`/`npc`/`pilot`, the exact set RAW
 *       grants Overwatch to (see below);
 *   (g) the mover's Feint (`t_duelist:2`, {@link feintedBy}) does not name the watcher. The
 *       authored compiler below skips a Feint-named caster the same way.
 *
 * **(f) — who may take Overwatch at all, and why it's `isActor` and not `isCharacter`.** Overwatch
 * is a *reaction*, and a reaction is an *action taken by a character* — so the watcher loop needs
 * a capability gate, not just a range test. RAW, in three steps:
 *   - **Objects and deployables are not characters and take no actions.** Core p.63: "An object is
 *     anything that isn't a character… includes both terrain features… and pieces of gear that have
 *     been placed down or deployed (e.g., shield generators and landmines)." A deployed cover panel
 *     is exactly that, so it can never be a watcher. Without this gate a Jericho Deployable Cover
 *     gets offered the shot, because `overwatchWeaponThreats`'s weaponless-watcher branch hands
 *     the empty-loadout object an unarmed strike at Threat 1.
 *   - **`drone` is excluded too, even though `isCharacter` admits it.** Core p.63 does call drones
 *     characters ("non-sentient characters like drones and automated turrets can also be
 *     characters"), which is why {@link isCharacter} includes them — but that is about being
 *     *targetable/save-making*, not about *acting*. Core p.72 grants the default reaction only to
 *     mechs ("By default, all mechs can take the BRACE and OVERWATCH reactions"), Core p.76 extends
 *     it to pilots on foot ("Pilots on foot can take the following actions and reactions: … and
 *     OVERWATCH"), and neither list reaches drones; the DRONE tag itself (Core p.105) says "By
 *     default, DRONES can't take actions or move." The printed weaponized case confirms the shape:
 *     GMS **Turret Drones** grants the TURRET ATTACK reaction to *the user's mech*, not to the
 *     drone. So an armed drone does NOT get a built-in Overwatch offer. A future drone that really
 *     does print its own reaction gets it through the **authored** lane ({@link
 *     AUTHORED_MOVE_REACTION_ID} / `reactionRegistry.ts`), which is where "otherwise specified"
 *     content belongs — never by widening this built-in.
 *   - `isActor` (`mech`/`npc`/`pilot`) is therefore the exact RAW set, and it's the canonical
 *     predicate rather than a fresh `kind` enumeration (capabilities over kinds).
 *
 * **This gate is on KIND, not on an empty loadout.** `overwatchWeaponThreats`'s
 * unarmed-strike fallback stays exactly as it is: a weaponless **mech or pilot** still threatens 1
 * and is still offered the shot (RAW p.76 — a pilot's Overwatch fires FIGHT, which an unarmed pilot
 * can still make). Deleting that branch would fix the symptom by breaking a different rule.
 *
 * **No `outcome` — this offer only *detects and offers*.** Unlike `reactionRegistry.ts`'s
 * `ReactionOffer` (which composes a resolved `ReactionOutcome` straight into the triggering
 * attack), a {@link MoveCheckpointOffer} carries no outcome hook at all: "take" is a SIBLING
 * resolution (the watcher's own `request_attack {reaction:true}` — the existing manual
 * Overwatch path, unchanged) that the watcher's client runs to completion BEFORE re-submitting
 * the recorded "take" answer — the interleaving contract. A `take` answer
 * doesn't need to be distinguished from `decline` at THIS layer — {@link gatherMoveCheckpoint}
 * treats any recorded option as "answered, move on" — the real consequence (the watcher's named
 * `"overwatch"` cap spent) is what makes a re-raised offer naturally ineligible on the next
 * re-gather (item (d) above), which is exactly how a "spent manual Overwatch makes the offer
 * ineligible" and "decline-then-manual-fire still works" both fall out with zero extra state.
 *
 * **Deterministic order**: `compile` itself walks watcher ids ascending (a stable, arbitrary
 * internal order — mirrors `reactionRegistry.ts`'s `compileProtectorOffers`); the REAL order
 * (initiative, then id) is enforced once, centrally, by {@link gatherMoveCheckpoint}'s own sort
 * over every consumer's flattened offers — this consumer does not need its own comparator.
 */
function compileOverwatchOffers(ctx: MoveCheckpointContext): MoveCheckpointOffer[] {
  const { state, declaredMove } = ctx;
  const mover = state.combatants[declaredMove.moverId];
  if (!mover) return [];
  if (hasStatus(mover.statuses, DISENGAGED_STATUS)) return []; // (e) — Disengaged movement provokes nothing.
  const moverFp = moverFootprintAtFrom(state, declaredMove.from, mover.base.size);
  if (moverFp.length === 0) return []; // no footprint to threaten (an off-grid/degenerate `from`).
  const offers: MoveCheckpointOffer[] = [];
  for (const watcherId of Object.keys(state.combatants).sort()) {
    if (watcherId === declaredMove.moverId) continue;
    const watcher = state.combatants[watcherId]!;
    if (!isActor(watcher)) continue; // (f) — objects/deployables/drones take no reactions (RAW p.63/72/76/105).
    if (!watcher.pos) continue; // (b) — unplaced, nothing to threaten from.
    if (sideOf(watcher) === sideOf(mover)) continue; // (a) — not hostile.
    const reach = overwatchReachAt(state, { watcher, targetCells: moverFp }); // (c), Ordnance- and Superheavy-excluded.
    if (reach === undefined || reach.weapons.length === 0) continue;
    if (!reactionAvailable(state, watcherId, { name: OVERWATCH_CHECKPOINT_ID })) continue; // (d).
    if (cannotMakeAttacks({ state, actorId: watcherId, overlay: ctx.overlay }) !== undefined) continue; // the rig's Unarmed: the take would be refused.
    if (hasLiveMoveReactionDecline(state, { watcherId, moverId: mover.id, consumerId: OVERWATCH_CHECKPOINT_ID })) continue; // declined earlier this turn.
    if (feintedBy(mover, watcherId)) continue; // the mover's Feint names this watcher.
    const watcherName = watcher.name;
    const moverName = mover.name;
    offers.push({
      id: `${OVERWATCH_CHECKPOINT_ID}:${watcherId}`,
      watcherId,
      who: () => watcherId,
      promptId: () => moveCheckpointPromptId(OVERWATCH_CHECKPOINT_ID, watcherId),
      prompt: () => `${watcherName} may fire Overwatch at ${moverName} ${formatOverwatchReach(reach)} before it moves — take the shot?`,
      title: "Overwatch",
      options: [
        { id: OVERWATCH_TAKE_OPTION, label: "Take Overwatch" },
        { id: OVERWATCH_DECLINE_OPTION, label: "Decline" },
      ],
      overwatchReach: reach,
    });
  }
  return offers;
}

/** The fixed member itself — appended to {@link MOVE_CHECKPOINT_CONSUMERS} below. */
export const OVERWATCH_CHECKPOINT_CONSUMER: MoveCheckpointConsumer = {
  id: OVERWATCH_CHECKPOINT_ID,
  compile: compileOverwatchOffers,
};

// ── authored move-checkpoint offer compiler ───────────────────────────────────────────────

/** The consumer id (also the namespace every compiled offer's prompt id carries, via {@link moveCheckpointPromptId}). */
export const AUTHORED_MOVE_REACTION_ID = "authored_move_reaction";
/** The recorded-answer option ids the offer presents. "Take" does not itself resolve the shot — see this consumer's own doc. */
export const MOVE_REACTION_TAKE_OPTION = "take";
export const MOVE_REACTION_DECLINE_OPTION = "decline";

/**
 * **The authored move-checkpoint reaction-offer compiler** — the second {@link
 * MoveCheckpointConsumer}, appended alongside {@link OVERWATCH_CHECKPOINT_CONSUMER}. Where
 * Overwatch is a built-in offered to a HOSTILE WATCHER, this compiles an offer authored onto the
 * MOVER's own active statuses and routes it to a THIRD PARTY — the status instance's own `source`
 * (the combatant who applied it, e.g. Covering Fire's caster) — never the mover itself and never a
 * fourth `EffectContext.actors` slot (the actor set is closed).
 *
 * **Gather:** {@link gatherStatusHandlers} off the MOVER's own `c.statuses` (the mark lives ON
 * the mover, exactly like `Covering Fire Mark`'s non-optional on_move auto-clear clause — see
 * `resolve.ts`'s `resolveMoveTrigger`), filtered to `trigger: "on_move"` AND `optional: true`.
 * **This is a DIFFERENT clause than the auto-clear one** — a status can carry BOTH an `on_move`
 * clause with `optional` unset (the automatic self-clear `resolveMoveTrigger` fires) and a
 * SEPARATE `on_move` clause with `optional: true` (this offer). `resolveMoveTrigger`'s own gather
 * excludes `optional: true` handlers (mirroring `reactionRegistry.ts`'s identical
 * on_damage_taken automatic/optional split) so the two never double-fire the same clause.
 *
 * **Eligibility, mirroring {@link compileOverwatchOffers}'s gate shape:**
 *   (a) the mover is not {@link DISENGAGED_STATUS} (RAW: Disengaged movement "does not provoke
 *       reactions" — the same general rule Overwatch's own gate (e) encodes, generalized here
 *       to every move-provoked reaction, not just Overwatch specifically);
 *   (b) the handler's own `carrierSource` (the mark's `source` — the caster) is present AND
 *       still a live combatant — a GM-applied, source-less instance, or a since-removed caster,
 *       has no one to route an offer to and is silently skipped (never an offer), mirroring the
 *       `has_status` with `source: "carrier"` predicate's own absent-means-false convention;
 *   (c) the clause's own `condition` (if any), evaluated against a {@link PredicateContext}
 *       carrying a move-family `event` payload (`distance` computed the SAME way
 *       `resolveMoveTrigger` does, `worldFor(state).distance(from, to)`) and `carrierSource` — an
 *       OMITTED condition fires unconditionally, mirroring {@link
 *       import("./effects/atoms").TriggeredEffect.condition}'s own doc;
 *   (d) {@link reactionAvailable} for the CASTER's own named reaction (`h.name` — the clause's
 *       own `name`, falling back to the carrying status's name, exactly {@link
 *       compileProtectorOffers}'s `reactionName` idiom) — folds in the global reaction slot, the
 *       1/round named cap, status/held-action legality, so a taken (cap spent) OR a since-
 *       ineligible (Jammed, out of reactions) offer stops re-raising on a LATER declared move.
 *
 * **No `outcome` — this offer only *detects and offers*, identical to Overwatch's own shape.**
 * "Take" is the existing `request_attack{reaction:true}` sibling (the caster fires from their
 * own inspector, weapon chosen and re-validated there) — no new attack machinery here. A
 * `take`/`decline` answer isn't distinguished at this layer; {@link gatherMoveCheckpoint} treats
 * any recorded option as "answered, move on," and the real consequence (the caster's named
 * reaction spent) is what naturally makes a re-raised offer ineligible on the next re-gather
 * (gate (d) above) — the SAME zero-extra-state suppression Overwatch's own doc describes.
 */
// **Deliberately status-only, NOT `gatherHandlersAt`** — same rationale as `reactionRegistry.ts`'s
// `compileAuthoredOffers`: this gather needs `Handler.name`/`.carrierSource` (gates (b)/(d) above),
// fields only the status vector threads.
function compileAuthoredMoveReactionOffers(ctx: MoveCheckpointContext): MoveCheckpointOffer[] {
  const { state, declaredMove } = ctx;
  const mover = state.combatants[declaredMove.moverId];
  if (!mover) return [];
  if (hasStatus(mover.statuses, DISENGAGED_STATUS)) return []; // (a) — Disengaged movement provokes nothing.
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  const declaredMoveDistance = declaredMove.spaces ?? worldFor(state).distance(declaredMove.from, declaredMove.to);
  const handlers = gatherStatusHandlers(state, declaredMove.moverId, overlay);
  const offers: MoveCheckpointOffer[] = [];
  handlers.forEach((h, idx) => {
    if (h.effect.on !== "move" || h.effect.optional !== true || h.effect.bystander === true) return; // a bystander clause is the observed-move window's.
    const casterId = h.carrierSource;
    if (casterId === undefined) return; // (b) — no source stamped, nobody to route the offer to.
    const caster = state.combatants[casterId];
    if (!caster) return; // (b) — the caster is since-removed (defensive).
    const pctx: PredicateContext = {
      state,
      actors: { self: declaredMove.moverId, target: declaredMove.moverId },
      event: { family: "move", distance: declaredMoveDistance, from: declaredMove.from, to: declaredMove.to, voluntary: declaredMove.voluntary ?? true },
      carrierSource: casterId,
    };
    // This compiler is a bespoke gate (NOT `fireTrigger`), so it must evaluate `match` itself
    // too — the event-fact half of a gate (e.g. `t_heavy_gunner:1`'s Covering Fire ">1 space")
    // lives in `match`, not `condition`. Mirrors `fireTrigger`'s own match-then-condition order
    // (`triggerBus.ts`).
    if (h.effect.match && !evaluateMatch(h.effect.match, pctx.event)) return;
    if (h.effect.condition && !evaluatePredicate(h.effect.condition, pctx)) return; // (c).
    const reactionName = h.name ?? h.source;
    if (!reactionAvailable(state, casterId, { name: reactionName })) return; // (d).
    if (hasLiveMoveReactionDecline(state, { watcherId: casterId, moverId: mover.id, consumerId: AUTHORED_MOVE_REACTION_ID })) return; // declined earlier this turn.
    if (feintedBy(mover, casterId)) return; // the mover's Feint names this caster.
    const moverName = mover.name;
    const casterName = caster.name;
    const splitCells = coveringFireSplitCells(state, reactionName, declaredMove.path);
    offers.push({
      id: `${AUTHORED_MOVE_REACTION_ID}:${declaredMove.moverId}#${idx}`,
      watcherId: casterId,
      who: () => casterId,
      promptId: () => moveCheckpointPromptId(AUTHORED_MOVE_REACTION_ID, casterId),
      prompt: () =>
        splitCells.length > 0
          ? `${casterName} may fire a reaction attack against ${moverName}'s move — take the shot now, or pick a cell along the path to shoot from there?`
          : `${casterName} may fire a reaction attack against ${moverName}'s move — take the shot?`,
      title: "Reaction attack",
      options: [
        { id: MOVE_REACTION_TAKE_OPTION, label: "Take the shot" },
        { id: MOVE_REACTION_DECLINE_OPTION, label: "Decline" },
        ...splitCells.map((cell) => ({ id: splitOptionId(state, cell), label: `Shoot at ${worldFor(state).key(cell)}` })),
      ],
    });
  });
  return offers;
}

/**
 * The cells a Covering Fire offer may split the move at (`landing-zone-full-automation` Q5, Core
 * p.97: "You can make this attack at any point during their movement"): every cell of the declared
 * path after the first, each once. The first is "take the shot" before the move. Empty for any
 * other authored reaction, and for a move with no route.
 */
function coveringFireSplitCells(state: EncounterState, reactionName: string, path: readonly Cell[] | undefined): Cell[] {
  if (reactionName !== COVERING_FIRE_REACTION || path === undefined) return [];
  const world = worldFor(state);
  const seen = new Set<string>([world.key(path[0]!)]);
  const cells: Cell[] = [];
  for (const cell of path.slice(1)) {
    const key = world.key(cell);
    if (seen.has(key)) continue;
    seen.add(key);
    cells.push(cell);
  }
  return cells;
}

/** The prefix of a Covering Fire offer's split-cell answers: `at:<cell key>`. */
export const SPLIT_OPTION_PREFIX = "at:";

/** The option id that splits a move at `cell`. */
export function splitOptionId(state: EncounterState, cell: Cell): string {
  return `${SPLIT_OPTION_PREFIX}${worldFor(state).key(cell)}`;
}

/**
 * The consumer id of the "take the shot now" prompt a split move halts on, once the mover holds on
 * the picked cell. Not a registered consumer: nothing compiles it. It borrows the checkpoint
 * prompt-id shape so the shooter's answer attributes to the mover and routes like any other offer.
 */
export const COVERING_FIRE_SPLIT_ID = "covering_fire_split";

/**
 * The split a re-submitted move's answers ask for: the first compiled offer whose recorded answer
 * is one of its split cells. `at` is that cell's index on `ctx.declaredMove.path` (its first
 * occurrence). `undefined` when no answer splits the move.
 */
export function splitMoveAnswer(
  ctx: MoveCheckpointContext,
  consumers: readonly MoveCheckpointConsumer[] = MOVE_CHECKPOINT_CONSUMERS,
): { shooterId: string; at: number } | undefined {
  const path = ctx.declaredMove.path;
  if (path === undefined || ctx.choices === undefined) return undefined;
  for (const offer of compileMoveCheckpointOffers(ctx, consumers)) {
    const answer = ctx.choices[offer.promptId(ctx)];
    if (answer === undefined || !answer.startsWith(SPLIT_OPTION_PREFIX) || !offer.options.some((o) => o.id === answer)) continue;
    const at = path.findIndex((cell, i) => i > 0 && splitOptionId(ctx.state, cell) === answer);
    if (at > 0) return { shooterId: offer.watcherId, at };
  }
  return undefined;
}

/** The fixed member itself — appended to {@link MOVE_CHECKPOINT_CONSUMERS} below. */
export const AUTHORED_MOVE_REACTION_CHECKPOINT_CONSUMER: MoveCheckpointConsumer = {
  id: AUTHORED_MOVE_REACTION_ID,
  compile: compileAuthoredMoveReactionOffers,
};

/**
 * The fixed, deterministically-ordered list of checkpoint-consumer types the `request_move`
 * pre-apply pass consults ({@link gatherMoveCheckpoint}'s production default). A new consumer is a
 * plain list append, never a rewire of {@link gatherMoveCheckpoint}'s seam.
 *
 * **The AUTOMATIC (non-optional) authored `on_move` bus point does NOT append here.** It's a
 * DISTINCT mechanism — the trigger bus (`effects/triggerBus.ts`'s `fireTrigger`), fired from
 * `backend/src/encounterRoom.ts`'s `fireMoveTrigger` once this checkpoint has cleared, never a
 * {@link MoveCheckpointConsumer}. The two are siblings at the same pre-move point, not one riding
 * the other's registry — see `fireMoveTrigger`'s own doc for why. {@link
 * AUTHORED_MOVE_REACTION_CHECKPOINT_CONSUMER} does NOT contradict this: it gathers a DIFFERENT
 * (`optional: true`) clause off the same status (`compileAuthoredMoveReactionOffers`'s own doc) —
 * an authored *offer*, never the automatic trigger-bus fire, so it genuinely belongs in this
 * registry the way Overwatch does.
 */
export const MOVE_CHECKPOINT_CONSUMERS: readonly MoveCheckpointConsumer[] = [
  OVERWATCH_CHECKPOINT_CONSUMER,
  AUTHORED_MOVE_REACTION_CHECKPOINT_CONSUMER,
  INTERRUPT_CONSUMER,
];

/**
 * The namespace prefix every {@link MoveCheckpointOffer.promptId} carries (via {@link
 * moveCheckpointPromptId}) — how the backend's cross-actor attribution seam
 * (`isMoveCheckpointPromptId`/`moveCheckpointWatcherId`, `backend/src/encounterRoom.ts`'s
 * `resolutionAttribution`) tells a watcher's checkpoint answer apart from an ordinary same-actor
 * recorded choice, without coupling to a fixed consumer-id list (mirrors `reactionRegistry.ts`'s
 * `AUTHORED_OFFER_ID_SHAPE` recognize-by-shape approach, simplified since every checkpoint offer
 * — built-in or authored — uses this ONE shape, unlike the reaction registry's built-in-vs-
 * authored id-shape split).
 */
const MOVE_CHECKPOINT_PROMPT_PREFIX = "move_checkpoint:";

/**
 * Build the stable prompt id for a `(consumerId, watcherId)` pairing — `<prefix><consumerId>:
 * <watcherId>`. Every {@link MoveCheckpointConsumer}'s compiled {@link MoveCheckpointOffer} MUST
 * build its `promptId` through this helper (not by hand) so {@link isMoveCheckpointPromptId}/
 * {@link moveCheckpointWatcherId} recognize it.
 */
export function moveCheckpointPromptId(consumerId: string, watcherId: string): string {
  return `${MOVE_CHECKPOINT_PROMPT_PREFIX}${consumerId}:${watcherId}`;
}

/**
 * Is `key` a recorded answer to a **movement-checkpoint** prompt (built via {@link
 * moveCheckpointPromptId})? The backend's cross-actor routing/attribution seam
 * (`resolutionAttribution`, mirroring its `isReactionPromptId` use) keys on
 * this to tell a watcher's checkpoint answer apart from an ordinary same-actor recorded choice,
 * so a WATCHER's re-submitted answer attributes to the MOVER (the parked intent's own source),
 * not the watcher's own socket.
 */
export function isMoveCheckpointPromptId(key: string): boolean {
  return key.startsWith(MOVE_CHECKPOINT_PROMPT_PREFIX);
}

/**
 * The **decider** (watcher) combatant id a movement-checkpoint prompt key names, or `undefined`
 * for a non-checkpoint key. Read back generically off the `<prefix><consumerId>:<watcherId>`
 * shape {@link moveCheckpointPromptId} builds — the suffix after the LAST colon, so a
 * consumer id itself may not contain a colon (matching every entity/combatant id's own
 * no-colon convention elsewhere in this codebase).
 */
export function moveCheckpointWatcherId(key: string): string | undefined {
  if (!isMoveCheckpointPromptId(key)) return undefined;
  const rest = key.slice(MOVE_CHECKPOINT_PROMPT_PREFIX.length);
  const lastColon = rest.lastIndexOf(":");
  return lastColon === -1 ? undefined : rest.slice(lastColon + 1);
}

/**
 * The combatant whose move a movement-checkpoint prompt's echoed intent holds: a `request_move`'s
 * mover, or the actor of a `request_activate` whose own walk provokes reactions (Impale,
 * `landing-zone-full-automation` Q7). `undefined` for any other intent.
 */
export function checkpointMoverId(intent: EncounterIntent): string | undefined {
  if (intent.type === "request_move") return intent.combatantId;
  if (intent.type === "request_activate") return intent.actor;
  return undefined;
}

/**
 * The **consumer** id a movement-checkpoint prompt key names, or `undefined` for a non-checkpoint
 * key. Sibling reader to {@link
 * moveCheckpointWatcherId} — same `<prefix><consumerId>:<watcherId>` shape, but reads the MIDDLE
 * segment (the `consumerId` argument {@link moveCheckpointPromptId} was built with): everything
 * between the fixed prefix and the LAST colon. Consumer ids carry no colon by convention (the same
 * no-colon convention every combatant/entity id already follows elsewhere in this codebase), so
 * the middle segment is unambiguous. This is the ONE discriminator an AI/autoplay policy consumer
 * needs to tell one registered {@link MoveCheckpointConsumer} apart from another off the prompt id
 * alone — see `@lancer-vtt/ai`'s `promptPolicy.ts`, the reader's first real consumer.
 */
export function moveCheckpointConsumerId(key: string): string | undefined {
  if (!isMoveCheckpointPromptId(key)) return undefined;
  const rest = key.slice(MOVE_CHECKPOINT_PROMPT_PREFIX.length);
  const lastColon = rest.lastIndexOf(":");
  return lastColon === -1 ? undefined : rest.slice(0, lastColon);
}

/**
 * The generation-order sort key for a compiled offer (this module's header doc): initiative
 * index (`state.order` position of {@link MoveCheckpointOffer.watcherId}), then the offer's own
 * stable `id` as a deterministic tiebreak (two offers on the same watcher, from different
 * consumer types — an edge case no shipped consumer produces, but the sort stays total either way).
 */
function compareOffers(
  state: EncounterState,
): (a: MoveCheckpointOffer, b: MoveCheckpointOffer) => number {
  const initiativeOf = (id: string): number => {
    const i = state.order.indexOf(id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i; // off-order (defensive) sorts last.
  };
  return (a, b) => {
    const ia = initiativeOf(a.watcherId);
    const ib = initiativeOf(b.watcherId);
    if (ia !== ib) return ia - ib;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  };
}

/**
 * Every registered consumer's offers for a declared move, sorted into the fixed deterministic order
 * (initiative, then id): the list {@link gatherMoveCheckpoint} consults, exported so a read-only
 * forecast names exactly the offers the room would halt on. Empty outside the acting phase.
 * `consumers` defaults to {@link MOVE_CHECKPOINT_CONSUMERS}, the same injection seam
 * `gatherMoveCheckpoint` takes.
 */
export function compileMoveCheckpointOffers(
  ctx: MoveCheckpointContext,
  consumers: readonly MoveCheckpointConsumer[] = MOVE_CHECKPOINT_CONSUMERS,
): MoveCheckpointOffer[] {
  // Checkpoint offers are reaction-shaped (Overwatch, RAW): they only exist during sequenced
  // combat, the same gate `gatherReaction` applies to the attack-side interception window. This
  // only suppresses the gather during un-sequenced setup (an idle encounter, no turn structure),
  // where a per-round reaction ledger is inert anyway.
  if (ctx.state.activation.turn.phase !== "acting") return [];

  const offers = consumers.flatMap((c) => c.compile(ctx));
  return [...offers].sort(compareOffers(ctx.state));
}

/**
 * Consult the movement checkpoint registry against a declared move — the `request_move`
 * pre-apply pass's gather (`backend/src/encounterRoom.ts`'s `applyMoveIntent`, ahead of the
 * `move_token` emission). Compiles every registered consumer's offers, sorts them into the fixed
 * deterministic order (initiative, then id), and consults them **one at a time** (the interleaving
 * contract): the first eligible, unanswered, interactive offer halts the WHOLE gather by
 * **throwing** a {@link NeedsDecisionSignal} — offers already resolved earlier in THIS call are
 * simply recompiled/reconsulted identically on the next round-trip (the gather is pure over
 * `(ctx, choices)`, so re-running it from scratch on every re-submit is cheap and deterministic).
 *
 * A **recorded** answer (`ctx.choices[promptId]` present and among the offer's own option ids)
 * is honored — the gather moves past that offer without re-raising it, regardless of WHICH
 * option was recorded (a "take" answer's actual consequence — spending the watcher's reaction —
 * happens off this gather entirely, in the watcher's own sibling `request_attack` resolution; by
 * the time that sibling resolution has committed and the mover's intent re-submits, a
 * re-eligibility check inside the offer's own `compile` naturally suppresses it, e.g.
 * `reactionAvailable` failing once a named reaction is spent).
 *
 * **Non-interactive with no recorded answer defaults to decline** (`ctx.interactive === false`
 * — replay, a scripted fixture, the CLI's non-interactive default): the gather never halts,
 * mirroring `gatherReaction`'s identical default. This is also how a move whose offers are all
 * pre-answered resolves with **zero** added latency — the common case.
 *
 * The optional `consumers` param IS the injection seam: production code calls this with no second
 * argument (defaulting to {@link MOVE_CHECKPOINT_CONSUMERS}); a test injects a synthetic consumer
 * list through the exact same parameter a real consumer uses.
 */
export function gatherMoveCheckpoint(
  ctx: MoveCheckpointContext,
  consumers: readonly MoveCheckpointConsumer[] = MOVE_CHECKPOINT_CONSUMERS,
): void {
  for (const offer of compileMoveCheckpointOffers(ctx, consumers)) {
    const promptId = offer.promptId(ctx);
    const recorded = ctx.choices?.[promptId];
    const optionIds = offer.options.map((o) => o.id);
    if (recorded !== undefined && optionIds.includes(recorded)) continue; // answered — move on.
    if (ctx.interactive) {
      throw new NeedsDecisionSignal(promptId, offer.prompt(ctx), { options: offer.options, who: offer.who(ctx), ...(offer.title !== undefined ? { title: offer.title } : {}) });
    }
    // Non-interactive, unanswered: the deterministic default — decline (pass-through, continue).
  }
}

/** Both built-in consumers' "decline" option ids (they are the same string today; listed so a rename of either can't silently stop recording). */
const DECLINE_OPTIONS: ReadonlySet<string> = new Set([OVERWATCH_DECLINE_OPTION, MOVE_REACTION_DECLINE_OPTION, INTERRUPT_DECLINE_OPTION]);

/**
 * The `record_move_reaction_decline` commands a re-submitted `request_move` (or an activation's own
 * walk that provokes reactions, Impale) owes: one per offer
 * the consumers compile against `ctx` right now whose prompt carries a recorded "decline". Keyed
 * off live offers, never off `choices` alone, so a stale answer for a watcher who is no longer
 * eligible — or whose decline is already on record, which the compilers skip — records nothing.
 * The GM backstop and the watcher's own slot answer through the same re-submit, so both land
 * here; so does an AI policy's decline, which re-submits the same way. A move by anyone but the
 * acting unit records nothing: the decline would have no turn of its own to end with.
 */
export function declineCommandsFor(
  ctx: MoveCheckpointContext,
  consumers: readonly MoveCheckpointConsumer[],
  actorId: string,
): RecordMoveReactionDeclineCommand[] {
  const { state, choices, declaredMove } = ctx;
  // Only the mover's OWN turn scopes a decline: the entry is dropped at the mover's end_turn, so
  // one recorded while someone else acts (a GM repositioning a non-actor) would outlive that turn
  // and silence the watcher on the mover's real turn later in the round.
  const turn = state.activation.turn;
  if (choices === undefined || turn.phase !== "acting" || turn.actor !== declaredMove.moverId) return [];
  const commands: RecordMoveReactionDeclineCommand[] = [];
  for (const offer of consumers.flatMap((c) => c.compile(ctx))) {
    const promptId = offer.promptId(ctx);
    const answer = choices[promptId];
    if (answer === undefined || !DECLINE_OPTIONS.has(answer)) continue;
    const consumerId = moveCheckpointConsumerId(promptId);
    const watcherId = moveCheckpointWatcherId(promptId);
    if (consumerId === undefined || watcherId === undefined) continue;
    if (commands.some((c) => c.watcherId === watcherId && c.consumerId === consumerId)) continue;
    commands.push({ type: "record_move_reaction_decline", actorId, watcherId, moverId: declaredMove.moverId, consumerId });
  }
  return commands;
}
